import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Minus, Check, MessageSquare, TrendingUp } from 'lucide-react';
import { MenuItem } from '../../../api/queries/menu.queries';
import { useCartStore } from '../../../stores/cart.store';
import { useModifiers } from '../../../api/queries/menu.queries';

interface DrinkDetailModalProps {
  drink: MenuItem;
  onClose: () => void;
  getLangValue: (multiLang: any) => string;
}

// Topping icons/images mapping
const toppingIcons: Record<string, string> = {
  pearl: '🧋',
  'cream cheese': '🧀',
  'choco': '🍫',
  'oat milk': '🥛',
  'crystal': '🧊',
  'egg pudding': '🍮',
  default: '🥤',
};

// Ice level icons mapping
const iceLevelIcons: Record<string, string> = {
  'normal': '🧊',
  'less': '❄️',
  'no': '☀️',
  'none': '☀️',
  default: '🧊',
};

function DrinkDetailModal({ drink, onClose, getLangValue }: DrinkDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { addItem } = useCartStore();
  const { data: allModifiers } = useModifiers();

  // State
  const [size, setSize] = useState<'small' | 'medium' | 'large'>(
    drink.availableSizes.includes('medium') ? 'medium' : drink.availableSizes[0] || 'medium'
  );
  const [sweetness, setSweetness] = useState<'0' | '25' | '50' | '70' | '100'>('100');
  const [selectedModifiers, setSelectedModifiers] = useState<
    {
      modifierGroupId: string;
      modifierOptionId: string;
      name: string;
      priceAdjustment: number;
    }[]
  >([]);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Filter modifiers for this drink
  // Handle both cases: modifierGroups can be array of IDs (strings) or populated objects
  const drinkModifierIds = drink.modifierGroups?.map((mg: any) =>
    typeof mg === 'string' ? mg : mg._id
  ) || [];
  const drinkModifiers = allModifiers?.filter((group) =>
    drinkModifierIds.includes(group._id)
  ) || [];

  // Separate toppings from other modifiers
  const toppingModifiers = drinkModifiers.filter((g) => g.type === 'topping');
  const otherModifiers = drinkModifiers.filter((g) => g.type !== 'topping' && g.type !== 'size' && g.type !== 'sweetness');

  // Calculate price
  const basePrice = drink.sizePricing?.[size] || drink.basePrice;
  const modifierTotal = selectedModifiers.reduce((sum, m) => sum + m.priceAdjustment, 0);
  const unitPrice = basePrice + modifierTotal;
  const totalPrice = unitPrice * quantity;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = () => {
    addItem({
      menuItemId: drink._id,
      categoryId: drink.categoryId,
      name: getLangValue(drink.name),
      quantity,
      size,
      sweetness,
      unitPrice: basePrice,
      modifiers: selectedModifiers,
      notes: notes || undefined,
    });
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Close only if clicking the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const toggleModifier = (groupId: string, option: any) => {
    const modifierId = option._id;
    const existing = selectedModifiers.find((m) => m.modifierOptionId === modifierId);

    if (existing) {
      setSelectedModifiers(selectedModifiers.filter((m) => m.modifierOptionId !== modifierId));
    } else {
      setSelectedModifiers([
        ...selectedModifiers,
        {
          modifierGroupId: groupId,
          modifierOptionId: modifierId,
          name: getLangValue(option.name),
          priceAdjustment: option.priceAdjustment,
        },
      ]);
    }
  };

  const getToppingIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    for (const [key, icon] of Object.entries(toppingIcons)) {
      if (lowerName.includes(key)) return icon;
    }
    return toppingIcons.default;
  };

  const getIceLevelIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    for (const [key, icon] of Object.entries(iceLevelIcons)) {
      if (lowerName.includes(key)) return icon;
    }
    return iceLevelIcons.default;
  };

  // Sweetness levels - 0% on left to 100% on right
  const sweetnessLevels = [
    { value: '0', label: '0%' },
    { value: '25', label: '25%' },
    { value: '50', label: '50%' },
    { value: '70', label: '70%' },
    { value: '100', label: '100%' },
  ];

  const sizeLevels = [
    { value: 'small', label: 'S', sublabel: i18n.language === 'th' ? 'เล็ก' : 'Small' },
    { value: 'medium', label: 'M', sublabel: i18n.language === 'th' ? 'กลาง' : 'Medium' },
    { value: 'large', label: 'L', sublabel: i18n.language === 'th' ? 'ใหญ่' : 'Large' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      {/* Modal wrapper for positioning close button */}
      <div className="relative">
        {/* Close button - Outside modal at top-right corner */}
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 z-20 w-11 h-11 rounded-full bg-red-100 hover:bg-red-500 text-red-500 hover:text-white transition-colors tap-highlight-none shadow-lg flex items-center justify-center"
          aria-label={t('common.close') || 'Close'}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="w-full max-w-4xl max-h-[85vh] rounded-2xl bg-card shadow-2xl flex overflow-hidden">
        {/* Left Side - Product Image */}
        <div className="relative w-2/5 bg-gradient-to-br from-primary/20 to-primary/5 flex-shrink-0">
          {drink.imageUrl ? (
            <img
              src={drink.imageUrl}
              alt={getLangValue(drink.name)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <span className="text-9xl">🥤</span>
            </div>
          )}

          {/* Trending badge */}
          {drink.isTrending && (
            <div className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-orange-500 px-3 py-1.5 text-sm font-medium text-white shadow-lg">
              <TrendingUp className="h-4 w-4" />
              <span>{t('pos.trending') || 'Trending'}</span>
            </div>
          )}
        </div>

        {/* Right Side - Options */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Product Info Header */}
          <div className="p-6 border-b">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold truncate">{getLangValue(drink.name)}</h2>
                {drink.description && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {getLangValue(drink.description)}
                  </p>
                )}
              </div>
              <p className="text-3xl font-bold text-primary shrink-0">
                {formatPrice(basePrice)}
              </p>
            </div>
          </div>

          {/* Options Content */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto">
            {/* Size Selection - Inline */}
            {drink.availableSizes.length > 1 && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold text-foreground w-24 shrink-0">
                  {t('pos.size') || 'Size'}
                </label>
                <div className="flex gap-3 flex-1">
                  {sizeLevels
                    .filter((s) => drink.availableSizes.includes(s.value as any))
                    .map((s) => {
                      const sizePrice = drink.sizePricing?.[s.value as 'small' | 'medium' | 'large'];
                      return (
                        <button
                          key={s.value}
                          onClick={() => setSize(s.value as any)}
                          className={`relative flex-1 rounded-xl border-2 py-3 px-4 font-medium transition-all tap-highlight-none ${
                            size === s.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background hover:bg-accent border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="text-xl font-bold">{s.label}</div>
                          {sizePrice && (
                            <div className="text-xs opacity-80 mt-0.5">{formatPrice(sizePrice)}</div>
                          )}
                          {size === s.value && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-card">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Sweetness Selection - Compact (0% to 100%) */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-semibold text-foreground w-24 shrink-0">
                {t('pos.sweetness') || 'Sweetness'}
              </label>
              <div className="flex gap-2 flex-1">
                {sweetnessLevels.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => setSweetness(level.value as any)}
                    className={`flex-1 rounded-lg border-2 py-2.5 px-2 transition-all tap-highlight-none text-center ${
                      sweetness === level.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-accent border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="text-sm font-semibold">{level.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Toppings - Grid */}
            {toppingModifiers.map((group) => (
              <div key={group._id}>
                <div className="flex items-center gap-2 mb-3">
                  <label className="text-sm font-semibold text-foreground">
                    {getLangValue(group.name)}
                  </label>
                  {group.isRequired && <span className="text-destructive text-xs">*</span>}
                  {group.maxSelection > 1 && (
                    <span className="text-xs text-muted-foreground">
                      ({t('pos.selectUpTo') || 'Max'} {group.maxSelection})
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {group.options.map((option) => {
                    const isSelected = selectedModifiers.some(
                      (m) => m.modifierOptionId === option._id
                    );
                    return (
                      <button
                        key={option._id}
                        onClick={() => toggleModifier(group._id, option)}
                        className={`relative rounded-xl border-2 p-3 transition-all tap-highlight-none flex items-center gap-2.5 ${
                          isSelected
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background hover:bg-accent border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="text-2xl">
                          {getToppingIcon(getLangValue(option.name))}
                        </span>
                        <div className="flex-1 text-left min-w-0">
                          <div className={`text-xs font-medium truncate ${isSelected ? 'text-primary' : ''}`}>
                            {getLangValue(option.name)}
                          </div>
                          <div className={`text-xs ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                            +{formatPrice(option.priceAdjustment)}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Other Modifiers - Grid layout matching Toppings */}
            {otherModifiers.map((group) => {
              const isIceLevel = getLangValue(group.name).toLowerCase().includes('ice');
              return (
                <div key={group._id}>
                  <div className="flex items-center gap-2 mb-3">
                    <label className="text-sm font-semibold text-foreground">
                      {getLangValue(group.name)}
                    </label>
                    {group.isRequired && <span className="text-destructive text-xs">*</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {group.options.map((option) => {
                      const isSelected = selectedModifiers.some(
                        (m) => m.modifierOptionId === option._id
                      );
                      const optionName = getLangValue(option.name);
                      return (
                        <button
                          key={option._id}
                          onClick={() => toggleModifier(group._id, option)}
                          className={`relative rounded-xl border-2 p-3 transition-all tap-highlight-none flex items-center gap-2.5 ${
                            isSelected
                              ? 'bg-primary/10 border-primary'
                              : 'bg-background hover:bg-accent border-border hover:border-primary/50'
                          }`}
                        >
                          {isIceLevel && (
                            <span className="text-2xl">
                              {getIceLevelIcon(optionName)}
                            </span>
                          )}
                          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>
                            {optionName}
                          </span>
                          {isSelected && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Notes Toggle */}
            {!showNotes ? (
              <button
                onClick={() => setShowNotes(true)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                <MessageSquare className="h-4 w-4" />
                <span>{t('pos.addNotes') || 'Add special instructions'}</span>
              </button>
            ) : (
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  {t('pos.notes') || 'Special Instructions'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('pos.notesPlaceholder') || 'Any special requests...'}
                  className="w-full rounded-xl border bg-background px-4 py-3 text-sm h-20 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  maxLength={200}
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Footer - Quantity & Add to Cart */}
          <div className="border-t bg-muted/30 p-5">
            <div className="flex items-center gap-5">
              {/* Quantity Control */}
              <div className="flex items-center gap-3 bg-background rounded-xl border p-1.5">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="w-11 h-11 rounded-lg hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors tap-highlight-none"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <span className="text-2xl font-bold w-10 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-11 h-11 rounded-lg bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors tap-highlight-none"
                >
                  <Plus className="h-5 w-5 text-primary" />
                </button>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="flex-1 rounded-xl bg-primary py-4 font-bold text-primary-foreground hover:bg-primary/90 transition-all active:scale-[0.98] tap-highlight-none flex items-center justify-center gap-3 text-lg shadow-lg"
              >
                <span>{t('pos.addToCart') || 'Add to Cart'}</span>
                <span className="bg-primary-foreground/20 px-4 py-1.5 rounded-lg font-bold">
                  {formatPrice(totalPrice)}
                </span>
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default DrinkDetailModal;
